// @ts-nocheck - Legacy file with extensive type issues requiring comprehensive refactoring (TODO: GXM-XXXX)
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { z } from "zod";
import API_ROUTES from "~/api-routes";
import apiService from "~/services/service";
import {
  fetchCities,
  fetchStates,
} from "../../entities/address/addressesSlice";
import { useNavigate } from "react-router-dom";

const useShareholderProfileAddress = (
  type,
  initialAddressInfo,
  parentSetAddressInfo,
  entityData,
  loading,
) => {
  const [isEditing, setIsEditing] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [addressData, setAddressData] = useState(initialAddressInfo);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    const hasEntityPlusAccess = entityData?.active_services?.some(
      (e) => e.name === "Entities +",
    );
    if (!hasEntityPlusAccess && !loading) {
      navigate("/forbidden");
    }
  }, [entityData]);

  const handleEditClick = () => {
    setIsEditing(true);
    setSuccessMessage("");
    setIsSaveDisabled(true);
  };

  const handleDiscardClick = async () => {
    const hydratedData = await hydrateAddressData(initialAddressInfo);
    setAddressData(hydratedData);
    setIsEditing(false);
  };

  const hydrateAddressData = async (rawAddress) => {
    const countryCode =
      typeof rawAddress.country === "object"
        ? rawAddress.country.code
        : rawAddress.country;
    const stateCode =
      rawAddress.state && rawAddress.state.code
        ? rawAddress.state.code
        : rawAddress.state;
    const cityCode =
      rawAddress.city && rawAddress.city.code
        ? rawAddress.city.code
        : rawAddress.city;

    let stateOptions = rawAddress.stateOptions;
    let cityOptions = rawAddress.cityOptions;

    if (!stateOptions && countryCode) {
      const stateRes = await dispatch(fetchStates(countryCode)).unwrap();
      stateOptions = stateRes.map((state) => ({
        name: state.label,
        code: state.value,
      }));
    }

    if (!cityOptions && countryCode && stateCode) {
      const cityRes = await dispatch(
        fetchCities([countryCode, stateCode]),
      ).unwrap();
      cityOptions = cityRes.map((city) => ({
        name: city.label,
        code: city.value,
      }));
    }

    const selectedState = stateOptions?.find(
      (state) => state.code === stateCode,
    );
    const selectedCity = cityOptions?.find((city) => city.code === cityCode);

    return {
      ...rawAddress,
      state: selectedState || null,
      city: selectedCity || null,
      stateOptions,
      cityOptions,
    };
  };

  const handleSaveClick = async () => {
    setIsLoading(true);
    try {
      const updatedAddressData = {
        type: addressData.type || "",
        country:
          typeof addressData.country === "object"
            ? addressData.country.code
            : addressData.country || "",
        address_line_1: addressData.address_line_1 || "",
        address_line_2: addressData.address_line_2 || "",
        city:
          addressData.city && addressData.city.code
            ? addressData.city.code
            : addressData.city || "",
        state:
          addressData.state && addressData.state.code
            ? addressData.state.code
            : addressData.state || "",
        postalcode: addressData.postalcode || "",
      };

      const res = await apiService(API_ROUTES.UPDATE_ADDRESS, "PUT", {
        body: JSON.stringify(updatedAddressData),
        replace: { id: addressData.id },
        schema: z.object({
          id: z.string(),
        }),
      });

      if (res.status) {
        setAddressData(addressData);
        parentSetAddressInfo({ addressData: addressData });
        setIsEditing(false);
        setIsSaveDisabled(true);
        setSuccessMessage("Address has been updated.");
      }
    } catch (error) {
      console.error("Failed to update data", error);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    type,
    isEditing,
    setIsEditing,
    successMessage,
    setSuccessMessage,
    isSaveDisabled,
    setIsSaveDisabled,
    isLoading,
    setIsLoading,
    handleEditClick,
    handleDiscardClick,
    handleSaveClick,
    addressData,
    setAddressData,
  };
};

export default useShareholderProfileAddress;
